import type { DraftState, SheetMapping, WizardItemType, WizardSampleGroup } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { CSVMappingResult } from '../../../View/Page/HomePage/Modals/CSVModalTypes.ts'
import type { PureMetadata } from '../../../Model/Sample/ISampleMetaData.ts'
import { prefillable } from '../primerComparison.ts'
import { hasKeyColumn, sheetRevision } from './sheetMapping.ts'
import { sheetMatchOf } from './sheetMatching.ts'
import { rowImport, sheetPrimers } from './sheetImport.ts'
import { fileSampleName } from '../sampleLabels.ts'
import { isArchiveOrder } from '../archive.ts'
import { writtenEstimate } from '../primerOrientation.ts'

export type ApplyEntry = {
  group: WizardSampleGroup
  itemId: string
  result: CSVMappingResult
  primerSource?: PrimerSource
  sheetKey?: string
  primers: string
  itemType: WizardItemType
  skipped: string[]
  /** The metadata fields this write sets. */
  fields: string[]
}

const NO_SHEET: { mapping: SheetMapping; extraColumns: Record<string, never> } = {
  mapping: { metadata: {}, reasons: {} },
  extraColumns: {}
}

const sheetRowOf = (draft: DraftState, group: WizardSampleGroup): Record<string, string> | undefined => {
  const sheet = draft.sheet && hasKeyColumn(draft.sheet.mapping) ? draft.sheet : undefined
  const rowIndex = sheet ? sheetMatchOf(draft)?.byGroup[group.key] : undefined
  return sheet && rowIndex !== undefined ? sheet.rows[rowIndex] : undefined
}

export type PrimerSource = 'sheet' | 'manual' | 'estimate'

/** The primers a group's sheet row declares, `undefined` per side when it has no row or declares none. */
export const sheetPrimersFor = (draft: DraftState, group: WizardSampleGroup): { forward?: string; reverse?: string } => {
  const row = sheetRowOf(draft, group)
  return row && draft.sheet ? sheetPrimers(row, draft.sheet.mapping) : {}
}

/** The primers someone gave for a group, per side: entered in the wizard, else its sheet row's. Never an estimate. */
export const declaredPrimers = (draft: DraftState, group: WizardSampleGroup): { forward?: string; reverse?: string } => {
  const fromSheet = sheetPrimersFor(draft, group)
  return {
    forward: group.manualPrimers?.forward ?? fromSheet.forward,
    reverse: group.manualPrimers?.reverse ?? fromSheet.reverse
  }
}

/**
 * The exact primers the wizard writes into a group's item, per side: entered in the wizard, else the
 * sheet row's, else a confident estimate from the reads, in the orientation it is written in.
 */
export const primersFor = (draft: DraftState, group: WizardSampleGroup): { forward?: string; reverse?: string; source?: PrimerSource } => {
  const fromSheet = sheetPrimersFor(draft, group)
  const manual = group.manualPrimers
  const oriented = writtenEstimate(draft, group)
  const estimated = prefillable(oriented) ? oriented : undefined
  const forward = manual?.forward ?? fromSheet.forward ?? estimated?.forward.primer ?? undefined
  const reverse = manual?.reverse ?? fromSheet.reverse ?? estimated?.reverse.primer ?? undefined
  if (!forward && !reverse) {
    return {}
  }
  const source: PrimerSource = manual?.forward || manual?.reverse ? 'manual' : fromSheet.forward && fromSheet.reverse ? 'sheet' : 'estimate'
  return { forward, reverse, source }
}

export const primerKeyOf = (primers: { forward?: string; reverse?: string }): string => `${primers.forward ?? ''}\u0000${primers.reverse ?? ''}`

const fieldsOf = (metadata: PureMetadata): string[] =>
  Object.entries(metadata)
    .filter(([, value]) => value !== undefined)
    .map(([field]) => field)

/** The primer sides the last write set that this one no longer has: written empty, which clears them. */
const primersLeft = (applied: string | undefined, primers: { forward?: string; reverse?: string }) => {
  const [forward, reverse] = (applied ?? '\u0000').split('\u0000')
  return {
    forward: primers.forward ?? (forward ? '' : undefined),
    reverse: primers.reverse ?? (reverse ? '' : undefined)
  }
}

/**
 * What each created item should receive: its sheet row (name, exact primers, metadata), else a confident
 * estimate for the primers and the file's name for the name. An item is written again only when that
 * changes.
 */
export const planSheetApply = (draft: DraftState): ApplyEntry[] => {
  /* an archive's rows are samples inside it, planned by planArchiveApply */
  if (isArchiveOrder(draft)) {
    return []
  }
  const sheet = draft.sheet && hasKeyColumn(draft.sheet.mapping) ? draft.sheet : undefined
  return Object.values(draft.groups).flatMap((group): ApplyEntry[] => {
    const itemId = group.item.itemId
    if (group.item.status !== 'created' || !itemId || group.applied?.status === 'pending') {
      return []
    }
    const row = sheetRowOf(draft, group)
    const { forward, reverse, source: primerSource } = primersFor(draft, group)
    const sheetKey = row && sheet ? sheetRevision(sheet) : undefined
    const primers = primerKeyOf({ forward, reverse })
    const itemType = group.item.itemType ?? group.itemType ?? 'SAMPLE'
    if (
      group.applied?.status === 'done' &&
      group.applied.sheetKey === sheetKey &&
      group.applied.primerSource === primerSource &&
      (group.applied.primers ?? primers) === primers &&
      (group.applied.itemType ?? itemType) === itemType
    ) {
      return []
    }
    const { result, skipped } = rowImport(
      row ?? {},
      row && sheet ? sheet : NO_SHEET,
      itemId,
      group.applied?.status === 'done' ? primersLeft(group.applied.primers, { forward, reverse }) : { forward, reverse },
      fileSampleName(draft, group)
    )
    const fields = fieldsOf(result.metadata)
    const cleared = (group.writtenFields ?? []).filter((field) => !fields.includes(field)) as (keyof PureMetadata)[]
    return [
      {
        group,
        itemId,
        result: cleared.length > 0 ? { ...result, clearedFields: cleared } : result,
        primerSource,
        sheetKey,
        primers,
        itemType,
        skipped,
        fields
      }
    ]
  })
}
